import { WorkLogEntry, DateRange } from '../../shared/types';
import { JiraServiceConfig, JiraServiceInterface } from './types';
import { API_ENDPOINTS } from '../../shared/constants';
import { apiFetch } from '../../shared/apiClient';
import * as XLSX from 'xlsx';

export class JiraService implements JiraServiceInterface {
  private config: JiraServiceConfig;

  constructor(config: JiraServiceConfig) {
    this.config = config;
  }

  // Поиск последней задачи
  async findLastTask(): Promise<number> {
    try {
      const response = await apiFetch(API_ENDPOINTS.JIRA.FIND_LAST_TASK);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.lastFoundTask || 0;
    } catch (error) {
      console.error('Error finding last task:', error);
      throw error;
    }
  }

  // Получение сохраненного номера последней задачи
  async getLastTaskNumber(): Promise<number> {
    try {
      const response = await apiFetch(API_ENDPOINTS.JIRA.LAST_TASK_NUMBER);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.lastTaskNumber || 0;
    } catch (error) {
      console.error('Error getting last task number:', error);
      throw error;
    }
  }

  // Сохранение номера последней задачи
  async saveLastTaskNumber(taskNumber: number): Promise<void> {
    try {
      const response = await apiFetch(API_ENDPOINTS.JIRA.LAST_TASK_NUMBER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ lastTaskNumber: taskNumber }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error saving last task number:', error);
      throw error;
    }
  }

  // Получение worklog записей по диапазону дат
  async getWorkLogEntriesByDateRange(dateRange: DateRange): Promise<WorkLogEntry[]> {
    try {
      const { startDate, endDate, fixVersion } = dateRange;
      const params = new URLSearchParams({ startDate, endDate });
      if (fixVersion && String(fixVersion).trim() !== '') {
        params.set('fixVersion', String(fixVersion).trim());
      }
      const response = await apiFetch(
        `${API_ENDPOINTS.JIRA.WORKLOG_DATE_RANGE}?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching worklog entries by date range:', error);
      throw error;
    }
  }

  // Получение всех worklog записей (постранично — один ответ без limit слишком тяжёлый для Supabase pooler)
  async getAllWorkLogEntries(): Promise<WorkLogEntry[]> {
    const pageSize = 3000;
    const maxPages = 250;
    try {
      const merged: WorkLogEntry[] = [];
      let page = 1;
      let total = Infinity;
      while (merged.length < total && page <= maxPages) {
        const { entries, total: t } = await this.getWorkLogEntriesPaginated(pageSize, page);
        total = t;
        const batch = Array.isArray(entries) ? entries : [];
        merged.push(...batch);
        if (batch.length === 0) break;
        page += 1;
        if (merged.length >= total) break;
      }
      return merged;
    } catch (error) {
      console.error('Error fetching all worklog entries:', error);
      throw error;
    }
  }

  // Получение worklog с пагинацией (newest first). Опционально startDate/endDate для фильтра по датам.
  async getWorkLogEntriesPaginated(
    limit: number,
    page: number,
    dateRange?: DateRange
  ): Promise<{ entries: WorkLogEntry[]; total: number }> {
    try {
      const params = new URLSearchParams({ limit: String(limit), page: String(page) });
      if (dateRange?.startDate && dateRange?.endDate) {
        params.set('startDate', dateRange.startDate);
        params.set('endDate', dateRange.endDate);
      }
      if (dateRange?.fixVersion && String(dateRange.fixVersion).trim() !== '') {
        params.set('fixVersion', String(dateRange.fixVersion).trim());
      }
      const response = await apiFetch(`${API_ENDPOINTS.JIRA.WORKLOG_ALL}?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return { entries: data.entries ?? [], total: data.total ?? 0 };
    } catch (error) {
      console.error('Error fetching paginated worklog entries:', error);
      throw error;
    }
  }

  // Сохранение данных worklog
  async saveWorkLogData(data: WorkLogEntry[]): Promise<void> {
    try {
      const response = await apiFetch(API_ENDPOINTS.JIRA.WORKLOG_SAVE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error saving worklog data:', error);
      throw error;
    }
  }

  // Восстановление данных worklog
  async restoreWorkLogData(): Promise<WorkLogEntry[]> {
    try {
      const response = await apiFetch(API_ENDPOINTS.JIRA.WORKLOG_RESTORE);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error restoring worklog data:', error);
      throw error;
    }
  }

  // Обновление базы данных
  async updateDatabase(options: {
    startFrom?: number;
    maxTaskNumber?: number;
    onProgress?: (progress: number) => void;
  }): Promise<void> {
    const { startFrom, maxTaskNumber, onProgress } = options;

    console.log('Starting database update with options:', { startFrom, maxTaskNumber });

    try {
      console.log('Sending POST request to /api/update-database...');
      const response = await apiFetch('/api/update-database', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startFrom,
          maxTaskNumber,
        }),
      });

      console.log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Обработка Server-Sent Events для прогресса
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body reader available');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let sawComplete = false;

      readLoop: while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim() === '') continue;

          if (line.startsWith('data: ')) {
            let data: Record<string, unknown>;
            try {
              data = JSON.parse(line.slice(6)) as Record<string, unknown>;
            } catch {
              continue;
            }

            if (data.type === 'error') {
              throw new Error(
                typeof data.message === 'string' ? data.message : 'Database update failed'
              );
            }

            if (data.type === 'heartbeat') {
              continue;
            }

            if (data.progress !== undefined && onProgress) {
              onProgress(data.progress as number);
            }

            if (data.type === 'complete') {
              sawComplete = true;
              break readLoop;
            }
          }
        }
      }

      if (!sawComplete) {
        throw new Error(
          'Connection closed before the update finished. The server may still be saving — check server logs, or run Full Update again.'
        );
      }
    } catch (error) {
      console.error('Error updating database:', error);
      throw error;
    }
  }

  // Экспорт в CSV
  exportToCSV(data: WorkLogEntry[]): void {
    const headers = [
      'Author',
      'Key',
      'Fix Version',
      'Start Date',
      'Time Spent (hrs)',
      'Status',
      'Type',
      'Summary',
    ];
    const csvContent = [
      headers.join(','),
      ...data.map((entry) =>
        [
          `"${entry.author}"`,
          `"${entry.key}"`,
          `"${entry.fixVersion || ''}"`,
          `"${entry.startDate}"`,
          entry.timeSpent.toFixed(1),
          `"${entry.status}"`,
          `"${entry.type}"`,
          `"${entry.summary}"`,
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `worklog_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Экспорт в Excel
  exportToExcel(data: WorkLogEntry[]): void {
    const headers = [
      'Author',
      'Key',
      'Fix Version',
      'Start Date',
      'Time Spent (hrs)',
      'Status',
      'Type',
      'Summary',
    ];

    const excelData = data.map((entry) => [
      entry.author,
      entry.key,
      entry.fixVersion || '',
      entry.startDate,
      Number(entry.timeSpent.toFixed(1)),
      entry.status,
      entry.type,
      entry.summary,
    ]);

    const totalTime = data.reduce((sum, entry) => sum + entry.timeSpent, 0);
    excelData.push(['Total', '', '', '', totalTime.toFixed(1), '', '', '']);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...excelData]);

    const colWidths = [
      { wch: 20 },
      { wch: 15 },
      { wch: 15 },
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 10 },
      { wch: 50 },
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Work Log Data');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `worklog_export_${new Date().toISOString().split('T')[0]}.xlsx`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
